import { ErrorBoundary } from '@sentry/react';
import { UserDetails } from '@statsbomb/parachute-types';
import { useAtomValue } from 'jotai';
import { ReactElement, ReactNode, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidEntityUserAtom } from '@/atoms/user';
import { USER_DETAILS_PATH } from '@/consts/client';
import { useLogout } from '@/hooks/useLogout';
import { FetchError } from '@/query/FetchError';
import { LicenseError } from './contentState/LicenseError';
import { GeneralErrorBoundaryFallback } from './errorBoundary/ErrorBoundaryFallback';
import { AppShellNoTextLoadingFallback } from './layout/AppShellFallback';

export const ErrorPage = ({
  errorType = 'no_tool_access',
  userDetails,
}: {
  errorType?: string;
  userDetails?: UserDetails | null;
}) => {
  const { t } = useTranslation('error');

  const errorMessage = t(`${errorType}.message`, {
    start_date: userDetails?.entity.start_date,
    end_date: userDetails?.entity.end_date,
  });

  return <LicenseError title={t(`${errorType}.title`)} description={errorMessage} />;
};

const LogoutAndRedirect = () => {
  const logout = useLogout();

  useEffect(() => {
    try {
      logout();
    } catch {
      window.location.href = window.location.origin;
    }
  }, [logout]);

  return <AppShellNoTextLoadingFallback />;
};

// Returns null for anything that isn't an API auth error, so each boundary keeps its own fallback
export const getApiErrorFallback = (
  error: Error,
  { notFound }: { notFound: 'any' | 'userDetails' },
): ReactElement | null => {
  if (!(error instanceof FetchError)) return null;

  if (error.status === 401) return <LogoutAndRedirect />;
  if (error.status === 403) return <ErrorPage />;
  // At the top level only a user-details 404 means "no licensing record"; other 404s must not read as "no licence"
  if (error.status === 404 && (notFound === 'any' || error.input === USER_DETAILS_PATH)) return <ErrorPage />;

  return null;
};

// Top-level fallback in Providers.tsx; lives here because Providers.tsx can't be imported under Jest
export const AppErrorBoundaryFallback = ({ error }: { error: Error }): ReactElement =>
  getApiErrorFallback(error, { notFound: 'userDetails' }) ?? <GeneralErrorBoundaryFallback />;

export const invalidEntityFallbackHandler = ({
  error,
  children,
}: {
  error: Error;
  children: ReactNode;
}): ReactElement =>
  // eslint-disable-next-line react/jsx-no-useless-fragment
  getApiErrorFallback(error, { notFound: 'any' }) ?? <>{children}</>;

const ShowErrorWithData = ({ children }: { children: React.ReactNode }) => {
  const { valid, error, userDetails } = useAtomValue(isValidEntityUserAtom);

  return valid ? children : <ErrorPage errorType={error} userDetails={userDetails} />;
};

export const HandleInvalidEntityUser = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<AppShellNoTextLoadingFallback />}>
    <ErrorBoundary fallback={({ error }) => invalidEntityFallbackHandler({ error, children })}>
      <ShowErrorWithData>{children}</ShowErrorWithData>
    </ErrorBoundary>
  </Suspense>
);
